import { useMemo, useRef, useEffect } from "react";
import CampaignForm from "./CampaignForm";
import {
  Box,
  HStack,
  Text,
  VStack,
  IconButton,
  Portal,
  useToast,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import AdSetForm from "./AdSetForm";
import AdCreativeForm from "./AdCreativeForm";
import {
  DisplayMarketingAdSet,
  DisplayMarketingCampaign,
  MarketingAd,
  CreateMarketingAdsetRequest,
} from "@common/interfaces/KYDMarketing";
import WizardNavigationBar from "./components/WizardNavigationBar";
import { FacebookPage, InstagramAccount, Step, StepProps } from "./types";
import ReviewForm from "./ReviewForm";
import { useForm } from "react-hook-form";
import { AdSetFormData } from "./forms/adset";
import adsetTransform from "./transform/adset";
import campaignTransform, { CampaignFormValues } from "./transform/campaign";
import { AdCreativeFormData } from "./forms/adcreative";
import adCreativeTransform from "./transform/adcreative";
import { useOutletContext } from "react-router-dom";
import { generateCallToActionOptions } from "src/utils/callToActionOptions";

const stepToComponent: Record<Step, React.ElementType<StepProps>> = {
  none: () => null,
  create_campaign: CampaignForm,
  edit_campaign: CampaignForm,
  create_adset: AdSetForm,
  edit_adset: AdSetForm,
  create_creative: AdCreativeForm,
  edit_creative: AdCreativeForm,
  review: ReviewForm,
};

const CreateCampaignWizard = ({
  onCreateCampaign,
  step,
  setDefaultCampaign,
  steps,
  setStep,
  defaultEditingAdset,
  onPublish,
  onCreateAd,
  onCreateAdCreative,
  onDeleteCreative,
  onDuplicateAdset,
  defaultCampaign,
  audiences,
  data,
  pixels,
  campaigns,
  instagramAccounts,
  adsets,
  adAccounts,
  onDeleteAdset,
  close,
  currency,
  search,
  pages,
  createUploadUrl,
  refetchAdsets,
  setDefaultEditingAdset,
  setDefaultEditingAd,
  loading,
  defaultAd,
  events,
}: {
  step?: Step;
  steps: Step[];
  events?: { id: string; name: string }[];
  data: {
    name: string;
    description: string;
    link: string;
  };
  setStep: (step: Step) => void;
  setDefaultCampaign: (campaign: DisplayMarketingCampaign) => void;
  close: () => void;
  onCreateCampaign: (
    campaign: any
  ) => Promise<{ campaign: DisplayMarketingCampaign } | null>;
  onDeleteAdset: (adset: DisplayMarketingAdSet) => Promise<{
    adset: null;
    campaign: DisplayMarketingCampaign;
  }>;
  onPublish: (
    campaign: any,
    extra?: { publish_all_adsets?: boolean }
  ) => Promise<void>;
  setDefaultEditingAdset: (adset: DisplayMarketingAdSet) => void;
  setDefaultEditingAd: (ad: MarketingAd) => void;

  defaultCampaign?: DisplayMarketingCampaign;
  defaultEditingAdset?: DisplayMarketingAdSet;
  defaultAd?: MarketingAd;

  onCreateAd: (ad: CreateMarketingAdsetRequest) => Promise<{
    adset: DisplayMarketingAdSet;
    campaign: DisplayMarketingCampaign;
  }>;
  onCreateAdCreative: (data: any) => Promise<{
    adset: DisplayMarketingAdSet;
    campaign: DisplayMarketingCampaign;
  }>;
  onDeleteCreative: (
    adset: DisplayMarketingAdSet,
    creative: MarketingAd
  ) => Promise<{
    adset: DisplayMarketingAdSet;
    campaign: DisplayMarketingCampaign;
  }>;
  onDuplicateAdset: (adset: DisplayMarketingAdSet) => Promise<{
    adset: DisplayMarketingAdSet;
    campaign: DisplayMarketingCampaign;
  }>;
  refetchAdsets: (campaignId: string) => Promise<void>;
  audiences?: { id: string; name: string }[];
  search?: StepProps["search"];
  pixels?: {
    id: string;
    name: string;
  }[];
  campaigns?: DisplayMarketingCampaign[];
  instagramAccounts?: InstagramAccount[];
  pages?: FacebookPage[];
  adsets?: {
    id: string;
    name: string;
  }[];
  adAccounts?: { id: string; name: string }[];
  currency?: string;
  createUploadUrl: (
    file: File,
    callback: (params: { signedUrl: string }) => void
  ) => void;
  loading?: {
    adsets: boolean;
    campaigns: boolean;
    audiences: boolean;
    pixels: boolean;
    preview: boolean;
    publish: boolean;
    adaccounts: boolean;
    search: boolean;
    instagramAccounts: boolean;
    pages: boolean;
  };
}) => {
  const currentStepFormRef = useRef<ReturnType<typeof useForm<any>> | null>(
    null
  );

  const toast = useToast();

  useEffect(() => {
    console.log(
      "CreateCampaignWizard ref changed:",
      currentStepFormRef.current
    );
  }, [currentStepFormRef.current]);

  const outletContext = useOutletContext<{
    portalRef?: React.RefObject<HTMLDivElement>;
  } | null>();
  const portalRef = outletContext?.portalRef;

  const _onCreateCampaign = async (campaign: CampaignFormValues) => {
    const payload = campaignTransform.output({ data: campaign });
    return onCreateCampaign(payload).then((response) => {
      const campaign = response?.campaign;
      if (campaign) {
        setDefaultCampaign(campaign);
        if (step === "create_campaign") {
          setStep("create_adset");
        } else if (step === "edit_campaign") {
          setStep("review");
        }
      }
      return response;
    });
  };

  const _onDeleteAdset = async (adset: DisplayMarketingAdSet) => {
    return onDeleteAdset(adset).then(async (r) => {
      await refetchAdsets(defaultCampaign?.id);
      setDefaultCampaign(r.campaign);

      return r;
    });
  };

  const _onDuplicateAdset = async (adset: DisplayMarketingAdSet) => {
    return onDuplicateAdset(adset).then(async (r) => {
      await refetchAdsets(defaultCampaign?.id);
      setDefaultCampaign(r.campaign);
      toast({
        title: "Adset duplicated",
        description: `Adset duplicated successfully. ${adset.name} is the duplicated adset`,
        status: "success",
      });

      return r;
    });
  };

  const _onDeleteCreative = async (
    adset: DisplayMarketingAdSet,
    creative: MarketingAd
  ) => {
    return onDeleteCreative(adset, creative).then(async (r) => {
      await refetchAdsets(defaultCampaign?.id);
      setDefaultCampaign(r.campaign);
      return r;
    });
  };

  const _onCreateAd = (ad: AdSetFormData) => {
    return onCreateAd(adsetTransform.output({ data: ad }))
      .then(async (r) => {
        if (step === "create_adset") {
          setStep("create_creative");
          setDefaultEditingAdset(r.adset);
        } else if (step === "edit_adset") {
          if (r.adset.status === "ACTIVE") {
            setStep("none");
          } else {
            setStep("review");
          }
        }
        await refetchAdsets(defaultCampaign?.id);
        setDefaultCampaign(r.campaign);

        return r;
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const _onCreateAdCreative = (adCreative: AdCreativeFormData) => {
    return onCreateAdCreative(
      adCreativeTransform.output({
        data: adCreative,
        adset: defaultEditingAdset,
        campaign: defaultCampaign,
        pages,
        instagramAccounts,
        callToActionOptions: generateCallToActionOptions(
          defaultCampaign.campaign_type
        ),
      })
    )
      .then(async (r) => {
        setDefaultCampaign(r.campaign);
        if (r.adset.status === "ACTIVE") {
          setStep("none");
        } else {
          setStep("review");
        }
        await refetchAdsets(defaultCampaign?.id);

        return r;
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const _onClose = () => {
    if (currentStepFormRef.current?.formState.isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to close?"
      );
      if (!confirmed) {
        return;
      }
    }

    close();
  };

  const _onPublish = (
    campaign: { id: string },
    extra: { publish_all_adsets?: boolean } = {}
  ) => {
    _onClose();
    return onPublish(campaign, extra);
  };

  const createSteps: Step[] = [
    "create_campaign",
    "create_adset",
    "create_creative",
    "review",
  ];
  const handlePrevious = () => {
    const index = createSteps.indexOf(step);
    const previousStep = createSteps[index - 1];

    if (step === "review") {
      setStep("none");
      return;
    }

    if (previousStep) {
      setStep(previousStep as Step);
      return;
    }

    setStep("none");
  };

  const stepToSubmit: Partial<
    Record<
      Step,
      (data: CampaignFormValues | AdSetFormData | any) => Promise<void | {
        adset?: DisplayMarketingAdSet;
        campaign: DisplayMarketingCampaign;
      }>
    >
  > = {
    create_campaign: _onCreateCampaign,
    edit_campaign: _onCreateCampaign,
    create_adset: _onCreateAd,
    edit_adset: _onCreateAd,
    create_creative: _onCreateAdCreative,
    edit_creative: _onCreateAdCreative,
  };

  const handleNext = () => {
    if (step === "review") {
      _onPublish(
        { id: defaultCampaign?.id },
        {
          publish_all_adsets: !defaultCampaign.external_id,
        }
      );
      return;
    }

    const stepOnSubmit = currentStepFormRef.current?.handleSubmit(
      (data) => {
        const result = stepToSubmit[step](data);

        return result;
      },
      (error: object) => {
        console.log("error", error);
        // Helper function to extract the first error message from nested error object
        const extractErrorMessage = (err: unknown): string | null => {
          if (!err || typeof err !== "object") {
            return null;
          }

          const errorObj = err as Record<string, unknown>;

          // Check if this object has a message property
          if ("message" in errorObj && typeof errorObj.message === "string") {
            return errorObj.message;
          }

          // Check root message first
          if (
            "root" in errorObj &&
            errorObj.root &&
            typeof errorObj.root === "object"
          ) {
            const rootMessage = extractErrorMessage(errorObj.root);
            if (rootMessage) return rootMessage;
          }

          // Recursively search through all properties
          for (const key in errorObj) {
            if (errorObj[key] && typeof errorObj[key] === "object") {
              const nestedMessage = extractErrorMessage(errorObj[key]);
              if (nestedMessage) return nestedMessage;
            }
          }

          return null;
        };

        const errorMessage =
          extractErrorMessage(error) ||
          "An error occurred. Please check your input.";

        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
        });
      }
    );

    console.log("StepOnSubmit", stepOnSubmit);

    if (stepOnSubmit) {
      stepOnSubmit();
    }
  };

  const isLoading = useMemo(() => {
    return loading?.campaigns || loading?.adaccounts || loading?.adsets;
  }, [loading]);

  const Component = useMemo(
    () => stepToComponent[step] || (() => null),
    [step]
  );

  if (step === "none" || !Component) {
    return null;
  }

  return (
    <Portal containerRef={portalRef}>
      <Box
        w="full"
        bg="white"
        position="absolute"
        zIndex={1000}
        top={0}
        left={0}
        right={0}
        bottom={0}
      >
        <HStack display="flex" w="full" flexGrow={1} h="full">
          <VStack
            display={["none", "flex"]}
            h="full"
            p={[4, 8]}
            borderRight="1px solid"
            borderColor="gray.200"
          >
            <IconButton
              aria-label="Close"
              icon={<CloseIcon color="gray.500" fontSize={12} />}
              size="md"
              variant="unstyled"
              rounded="full"
              onClick={_onClose}
              border="1px solid"
              _hover={{
                shadow: "md",
                bg: "gray.50",
              }}
              borderColor="gray.200"
              cursor="pointer"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="lg"
            />
          </VStack>

          <VStack
            w="full"
            spacing={0}
            overflowY="scroll"
            h="full"
            maxW="5xl"
            margin="0 auto"
          >
            <Box p={[2, 4]} w="full">
              <VStack w="full" spacing={[4, 8]}>
                <WizardNavigationBar
                  steps={steps}
                  currentStep={step}
                  isLoading={isLoading}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                />

                <VStack
                  w="full"
                  backgroundColor="gray.50"
                  rounded="xl"
                  border="1px solid"
                  boxShadow="md"
                  borderColor="gray.200"
                >
                  <Component
                    //@ts-ignore
                    ref={currentStepFormRef}
                    campaign={defaultCampaign}
                    navigateTo={(step) => {
                      if (step === "create_adset") {
                        setDefaultEditingAdset(null);
                      }

                      if (step === "create_creative") {
                        setDefaultEditingAd(null);
                      }

                      if (step in stepToComponent) {
                        setStep(step);
                        return;
                      }

                      console.log(`No step found for ${step}`);
                    }}
                    setDefaultEditingAdset={setDefaultEditingAdset}
                    adset={defaultEditingAdset}
                    steps={steps}
                    step={step}
                    ad={defaultAd}
                    setDefaultEditingAd={setDefaultEditingAd}
                    pages={pages}
                    onCreateCampaign={_onCreateCampaign}
                    onCreateAd={_onCreateAd}
                    onDeleteAdset={_onDeleteAdset}
                    onDeleteCreative={_onDeleteCreative}
                    onCreateAdCreative={_onCreateAdCreative}
                    onDuplicateAdset={_onDuplicateAdset}
                    onPublish={_onPublish}
                    loading={loading}
                    instagramAccounts={instagramAccounts}
                    adAccounts={adAccounts}
                    close={close}
                    back={handlePrevious}
                    next={handleNext}
                    audiences={audiences}
                    pixels={pixels}
                    search={search}
                    campaigns={campaigns}
                    adsets={adsets}
                    currency={currency}
                    createUploadUrl={createUploadUrl}
                    data={data}
                    events={events}
                  />
                </VStack>
              </VStack>
            </Box>
          </VStack>
        </HStack>
      </Box>
    </Portal>
  );
};

export default CreateCampaignWizard;
